import React from "react";
import Plot from 'react-plotly.js';
import {useTheme} from "@mui/material";
import {formatCurrency} from "../../utils/formatters";

// Bars smaller than this fraction of the largest value get outside labels.
const OUTSIDE_LABEL_RATIO = 0.35;

export const BarChartHorizontal = ({title, data}) => {
    const theme = useTheme();
    const dims = theme.dimensions;
    const indicatorTheme = theme.palette.indicator;
    const isProfitDrivers = title === 'Profit Drivers';

    if (!data || data.length === 0) return <div/>;

    const maxAbs = Math.max(...data.map((bar) => Math.abs(bar.value) || 0), 1);

    const getTextPosition = (value, visible) => {
        if (!visible) {
            return 'none';
        }
        return Math.abs(value) < maxAbs * OUTSIDE_LABEL_RATIO ? 'outside' : 'inside';
    };

    const getTextColor = (value, visible, barColor) => {
        if (!visible) {
            return 'transparent';
        }
        return Math.abs(value) < maxAbs * OUTSIDE_LABEL_RATIO ? barColor : '#ffffff';
    };

    const buildTrace = (traceData, color) => ({
        x: traceData.map((bar) => (bar.visible ? bar.value : null)),
        y: traceData.map((bar) => bar.name),
        type: 'bar',
        orientation: 'h',
        text: traceData.map((bar) => (
            bar.value === 0 ? formatCurrency(0, true) : formatCurrency(bar.value, true)
        )),
        textposition: traceData.map((bar) => getTextPosition(bar.value, bar.visible)),
        textangle: 0,
        insidetextanchor: 'end',
        textfont: {
            color: traceData.map((bar) => getTextColor(bar.value, bar.visible, color)),
            size: 12,
        },
        cliponaxis: false,
        marker: {color},
    });

    const positiveTraceData = data.map((bar) => ({
        ...bar,
        visible: bar.value > 0,
    }));
    const positiveTrace = buildTrace(
        positiveTraceData,
        isProfitDrivers ? indicatorTheme.green : indicatorTheme.blue
    );

    const negativeTraceData = data.map((bar) => ({
        ...bar,
        visible: bar.value < 0,
    }));
    const negativeTrace = buildTrace(negativeTraceData, indicatorTheme.red);

    const zeroTraceData = data.map((bar) => ({
        ...bar,
        visible: bar.value === 0,
    }));
    const zeroTrace = {
        x: zeroTraceData.map((bar) => (bar.visible ? 0 : null)),
        y: zeroTraceData.map((bar) => bar.name),
        type: 'bar',
        orientation: 'h',
        text: zeroTraceData.map((bar) => (bar.visible ? formatCurrency(0, true) : '')),
        textposition: zeroTraceData.map((bar) => (bar.visible ? 'outside' : 'none')),
        textangle: 0,
        textfont: {
            color: zeroTraceData.map((bar) => (bar.visible ? indicatorTheme.red : 'transparent')),
            size: 12,
        },
        cliponaxis: false,
        marker: {
            color: 'rgba(0,0,0,0)',
            line: {width: 0},
        },
        hoverinfo: 'skip',
        showlegend: false,
    };

    const plotData = [positiveTrace, negativeTrace, zeroTrace];

    const labelWidth = isProfitDrivers ? 70 : 200;
    const labelPadding = isProfitDrivers ? 0 : 10;
    const rightMargin = isProfitDrivers ? 88 : 30;

    return (
        <div>
            <Plot
                data={plotData}
                layout={{
                    width: dims.smallPanelWidth - 20,
                    height: isProfitDrivers ? 220 : 260,
                    margin: {t: 16, b: 10, l: labelWidth, r: rightMargin, pad: labelPadding},
                    xaxis: {
                        visible: false,
                        fixedrange: true,
                        zeroline: true,
                        zerolinewidth: 0,
                    },
                    yaxis: {
                        autorange: 'reversed',
                        fixedrange: true,
                        tickfont: {size: 13},
                    },
                    bargap: isProfitDrivers ? 0.55 : 0.2,
                    barmode: 'overlay',
                    showlegend: false,
                }}
                config={{displayModeBar: false}}
            />
        </div>
    );
};
